import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import { useUser } from '../../contexts/PioAgentUserContext';
import { getEndpoints } from './services/endpointService';

function ApiConfigPage() {
  const [apiKey, setApiKeyValue] = useState('');
  const [selectedEndpoint, setSelectedEndpoint] = useState('');
  const [customEndpoint, setCustomEndpoint] = useState('');
  const [useCustomEndpoint, setUseCustomEndpoint] = useState(false);
  const [endpoints, setEndpoints] = useState([]);
  const [isLoadingEndpoints, setIsLoadingEndpoints] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState(null);

  const { isAuthenticated, logout, apiConfigured, configureApi, apiKey: existingApiKey, workingEndpoint: existingEndpoint } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    // Comment out authentication check - always allow access
    // if (!isAuthenticated) {
    //   navigate('/');
    //   return;
    // }

    loadEndpoints();
    loadExistingConfig();
  }, [isAuthenticated, navigate]);

  const loadEndpoints = async () => {
    try {
      setIsLoadingEndpoints(true);
      const availableEndpoints = await getEndpoints();
      setEndpoints(availableEndpoints);

      if (availableEndpoints.length === 0) {
        console.warn('No endpoints available');
        setEndpoints(['chatbot.piosphere.ai', 'chatbotcost.piosphere.ai']);
      }
    } catch (error) {
      console.error('Failed to load endpoints:', error);
      setEndpoints(['chatbot.piosphere.ai', 'chatbotcost.piosphere.ai']);
    } finally {
      setIsLoadingEndpoints(false);
    }
  };

  const loadExistingConfig = () => {
    if (existingApiKey) {
      setApiKeyValue(existingApiKey);
    }
    if (existingEndpoint) {
      setSelectedEndpoint(existingEndpoint);
    }

    // Load custom endpoint settings from localStorage
    const storedUseCustom = localStorage.getItem('useCustomEndpoint') === 'true';
    const storedCustomEndpoint = localStorage.getItem('customEndpoint') || '';

    setUseCustomEndpoint(storedUseCustom);
    setCustomEndpoint(storedCustomEndpoint);

    // If using custom endpoint and it exists, set it as selected
    if (storedUseCustom && storedCustomEndpoint) {
      setSelectedEndpoint(storedCustomEndpoint);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalEndpoint = useCustomEndpoint ? customEndpoint : selectedEndpoint;

    if (!finalEndpoint || !apiKey) {
      setError('Please select or enter an endpoint and enter an API key');
      return;
    }

    // Save custom endpoint settings to localStorage
    localStorage.setItem('useCustomEndpoint', useCustomEndpoint.toString());
    if (useCustomEndpoint && customEndpoint) {
      localStorage.setItem('customEndpoint', customEndpoint);
    }

    setIsValidating(true);
    setError(null);

    try {
      const result = await configureApi(apiKey, finalEndpoint);

      if (result.success) {
        // Navigate to chat interface after successful configuration
        navigate('/chat');
      } else {
        setError(result.error || 'Invalid API key or endpoint');
      }
    } catch (err) {
      console.error('💥 Error configuring API:', err);
      setError('Connection error. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleLogout = () => {
    // Navigate to landing page (which redirects to API config)
    logout();
  };

  return (
    <Container component="main" maxWidth={false} disableGutters>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          background: 'linear-gradient(135deg, #50C878 30%, #111111 90%)',
          padding: 3,
        }}
      >
        <Paper
          elevation={6}
          sx={{
            p: 5,
            width: '100%',
            maxWidth: 500,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
          }}
        >
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h4" component="h1" color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
              API Configuration
            </Typography>

            <Typography variant="body1" color="text.secondary">
              Configure your API connection to access the platform.
            </Typography>
          </Box>

          <Typography
            variant="h6"
            color="text.primary"
            align="center"
            sx={{ mb: 3, fontWeight: 500 }}
          >
            Connect to API
          </Typography>

          {apiConfigured && (
            <Alert severity="info" sx={{ borderRadius: 2, mb: 3 }}>
              Your API is currently configured. You can update your settings below.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ borderRadius: 2, mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <FormControlLabel
              control={
                <Switch
                  checked={useCustomEndpoint}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setUseCustomEndpoint(enabled);
                    if (!enabled) {
                      // Clear custom endpoint when switching back to dropdown
                      setCustomEndpoint('');
                      // Reset to first available endpoint if none selected
                      if (!selectedEndpoint && endpoints.length > 0) {
                        setSelectedEndpoint(endpoints[0]);
                      }
                    }
                  }}
                  color="primary"
                />
              }
              label="Use Custom Endpoint"
              sx={{ mb: 2 }}
            />

            {useCustomEndpoint ? (
              <TextField
                margin="normal"
                required
                fullWidth
                label="Custom Endpoint"
                placeholder="e.g., your-api.example.com"
                value={customEndpoint}
                onChange={(e) => setCustomEndpoint(e.target.value)}
                helperText="Enter the full domain name of your API endpoint"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary',
                  },
                }}
              />
            ) : (
              <FormControl
                fullWidth
                margin="normal"
                required
                disabled={isLoadingEndpoints}
              >
                <InputLabel>Select Endpoint</InputLabel>
                <Select
                  value={selectedEndpoint}
                  onChange={(e) => setSelectedEndpoint(e.target.value)}
                  label="Select Endpoint"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  endAdornment={
                    isLoadingEndpoints && (
                      <CircularProgress size={20} sx={{ mr: 2 }} />
                    )
                  }
                >
                  {endpoints.map((endpoint) => (
                    <MenuItem key={endpoint} value={endpoint}>
                      <Typography>{endpoint}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              label="API Key"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKeyValue(e.target.value)}
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                },
              }}
            />

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              {(() => {
                const finalEndpoint = useCustomEndpoint ? customEndpoint : selectedEndpoint;
                const isDisabled = isValidating || !apiKey || !finalEndpoint || isLoadingEndpoints;
                return (
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isDisabled}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                      '&:hover': { bgcolor: 'primary.dark' },
                      position: 'relative'
                    }}
                  >

                {isValidating && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      marginLeft: '-12px',
                      marginTop: '-12px',
                    }}
                  />
                )}
                {isValidating ? 'Validating...' : 'Continue to Chat'}
                  </Button>
                );
              })()}

              {apiConfigured && (
                <Button
                  onClick={() => navigate('/chat')}
                  variant="outlined"
                  size="large"
                  sx={{
                    minWidth: '120px',
                    color: 'primary.main',
                    borderColor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.main',
                      color: 'white',
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  Back to Chat
                </Button>
              )}

              <Button
                onClick={handleLogout}
                variant="outlined"
                size="large"
                sx={{
                  minWidth: '120px',
                  color: 'error.main',
                  borderColor: 'error.main',
                  '&:hover': {
                    bgcolor: 'error.main',
                    color: 'white',
                    borderColor: 'error.main'
                  }
                }}
              >
                Logout
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default ApiConfigPage;
